import React, { useState, useEffect, useCallback } from 'react';
import { ActivityLogModel, MemberModel, BranchModel } from '../../../../models';
import { db } from '../../../../firebase';
import { collection, query, where, orderBy, limit, getDocs, startAfter, Timestamp } from 'firebase/firestore';
import LogList from './LogList';
import LogFilter from './LogFilter';
import AdminPageHeader from '../../frame/RightPanelHeader';
import { History } from 'lucide-react';

interface LogRootProps {
    currentAdmin: MemberModel | null;
    branches: BranchModel[];
}

const LogRoot: React.FC<LogRootProps> = ({ currentAdmin, branches }) => {
    const [logs, setLogs] = useState<ActivityLogModel[]>([]);
    const [lastDoc, setLastDoc] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    // Filters
    const [filterType, setFilterType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const targetBranchId = currentAdmin?.managedBranchId || currentAdmin?.branchId;

    const fetchLogs = useCallback(async (isLoadMore = false) => {
        if (!targetBranchId) return;
        if (isLoading) return;

        setIsLoading(true);
        try {
            let q = query(
                collection(db, 'logs'),
                where('branchId', '==', targetBranchId),
                orderBy('createdAt', 'desc'),
                limit(20)
            );

            // Apply Filters
            if (filterType !== 'all') {
                q = query(q, where('type', '==', filterType));
            }

            if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                q = query(q, where('createdAt', '>=', start.getTime()));
            }

            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                q = query(q, where('createdAt', '<=', end.getTime()));
            }

            // Pagination
            if (isLoadMore && lastDoc) {
                q = query(q, startAfter(lastDoc));
            }

            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                setHasMore(false);
                if (!isLoadMore) setLogs([]);
            } else {
                const newLogs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ActivityLogModel));
                setLastDoc(snapshot.docs[snapshot.docs.length - 1]);

                if (isLoadMore) {
                    setLogs(prev => [...prev, ...newLogs]);
                } else {
                    setLogs(newLogs);
                }
                setHasMore(snapshot.docs.length === 20);
            }

        } catch (error) {
            console.error("Error fetching logs:", error);
        } finally {
            setIsLoading(false);
        }
    }, [targetBranchId, filterType, startDate, endDate, lastDoc, isLoading]);

    // Initial Load & Filter Change
    useEffect(() => {
        setLastDoc(null);
        setHasMore(true);
        fetchLogs(false);
        // We only want to re-run this when filters change (excluding load more triggers)
        // Adjust dependency array carefully to avoid infinite loops or missing updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetBranchId, filterType, startDate, endDate]);

    // Handle Search Filter (Client-side for now as Firestore fuzzy search is hard)
    const filteredLogs = logs.filter(log => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        // Search in actor, details, apiKey
        return (
            log.actorName.toLowerCase().includes(term) ||
            log.details.toLowerCase().includes(term) ||
            (log.apiKeyPart && log.apiKeyPart.includes(term)) ||
            getActionLabel(log.action).includes(term)
        );
    });

    // Helper for search mapping
    function getActionLabel(action: string) {
        const labels: Record<string, string> = {
            'extract_omr': 'OMR 채점',
            'generate_counsel_reply': '상담 답변 생성',
            'generate_qa_answer': '질문 답변 생성',
            'create_schedules': '일정 생성',
            'update_schedule': '일정 수정',
            'delete_schedule': '일정 삭제',
            'bulk_delete_schedules': '일정 일괄 삭제',
        };
        return labels[action] || action;
    }

    const handleSearch = () => {
        // Trigger re-fetch if we implemented server-side search, 
        // currently it just filters client-side so maybe just reset pagination?
        // actually for client side filtering of *fetched* logs, we don't need to refetch.
        // But if user wants to find deep records, client side filtering only creates a view on *loaded* records.
        // Implementing detailed server search is complex without index. 
        // For now, let's keep it client-side on loaded data and maybe fetch more?
        // User requirement: "Search". 
        // A restart of fetch might be needed if I rely on exact match queries. 
        // For now, client side filtering is the safest MVP.
        setLogs([...logs]); // Force re-render
    };

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden">
            <AdminPageHeader
                title="활동 로그"
                icon={History}
                subtitle="시스템 사용 이력 및 API 활용 내역을 조회합니다"
            />

            <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 p-6 gap-4">
                <LogFilter
                    filterType={filterType}
                    setFilterType={setFilterType}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    onSearch={handleSearch}
                />

                <LogList
                    logs={filteredLogs}
                    isLoading={isLoading}
                    hasMore={hasMore}
                    onLoadMore={() => fetchLogs(true)}
                />
            </div>
        </div>
    );
};

export default LogRoot;
